import type {
  AssetProcessingActivityDetail,
  AssetProcessingActivityDetailDTO,
  AssetProcessingActivitySubmitDTO,
  AssetProcessingActivitySubmitInput,
  AssetProcessingActivityUpdateInput,
  OwnerAssetProcessingActivityReviewMeta,
  OwnerAssetProcessingActivityReviewMetaDTO,
  OwnerAssetProcessingPurpose,
  OwnerAssetProcessingPurposeDTO,
  OwnerAssetProcessingPurposeUpdateInput,
  OwnerCountryHandling,
  OwnerCountryHandlingCreateInput,
  OwnerCountryHandlingDeleteInput,
  OwnerCountryHandlingDTO,
  OwnerProcessingActivity,
  OwnerProcessingActivityDTO,
  OwnerProcessingPurpose,
  OwnerProcessingPurposeDTO,
  PatchedAssetProcessingActivityDTO,
  SoftDeleteAssetProcessingActivityDTO,
  SoftDeleteAssetProcessingActivityInput,
  OwnerAssetProcessingPurposeCreateOrUpdateDTO,
  OwnerCountryHandlingCreateDTO,
  OwnerCountryHandlingDeleteDTO,
  AddAssetProcessingActivityInput,
} from './assetProcessingActivities.types';

export const parseOwnerProcessingPurposeForFE = (dto: OwnerProcessingPurposeDTO): OwnerProcessingPurpose => ({
  id: dto.id,
  purposeText: dto.purpose_text,
  legalBasis: dto.legal_basis,
  legalBasisComment: dto.legal_basis_comment,
  retentionTrigger: dto.retention_trigger,
  retentionValue: dto.retention_value,
  retentionUnit: dto.retention_unit,
  retentionComment: dto.retention_comment,
});

export const parseOwnerProcessingActivityForFE = (dto: OwnerProcessingActivityDTO): OwnerProcessingActivity => ({
  id: dto.id,
  name: dto.name,
  description: dto.description,
  minimumSecurityMeasures: dto.minimum_security_measures,
  purposes: (dto.purposes ?? []).map(parseOwnerProcessingPurposeForFE),
});

export const parseOwnerAssetProcessingPurposeForFE = (dto: OwnerAssetProcessingPurposeDTO): OwnerAssetProcessingPurpose => ({
  id: dto.id,
  sourceProcessingPurposeId: dto.source_processing_purpose_id,
  applicabilityStatus: dto.applicability_status,
  applicabilityJustification: dto.applicability_justification,
  isNewPurposeNotAssessed: dto.is_new_purpose_not_assessed,
  retentionTrigger: dto.retention_trigger,
  retentionValue: dto.retention_value,
  retentionUnit: dto.retention_unit,
  retentionComment: dto.retention_comment,
  deletionOrAnonymisationRoutine: dto.deletion_or_anonymisation_routine,
});

export const parseOwnerCountryHandlingForFE = (dto: OwnerCountryHandlingDTO): OwnerCountryHandling => ({
  id: dto.id,
  countryCode: dto.country_code,
  handlingType: dto.handling_type,
  volume: dto.volume,
});

export const parseOwnerAssetProcessingActivityReviewMetaForFE = (
  dto: OwnerAssetProcessingActivityReviewMetaDTO | null | undefined,
): OwnerAssetProcessingActivityReviewMeta | null => {
  if (!dto) {
    return null;
  }

  return {
    id: dto.id,
    status: dto.status,
    clarificationComment: dto.clarification_comment,
    submittedAt: dto.submitted_at,
    submittedByMembershipId: dto.submitted_by_membership_id,
    submittedRevision: dto.submitted_revision,
  };
};

export const isAssetProcessingActivityDetailDto = (data: unknown): data is AssetProcessingActivityDetailDTO => {
  if (!data || typeof data !== 'object') {
    return false;
  }

  if (!('processing_activity' in data)) {
    return false;
  }

  const { processing_activity: processingActivity } = data as { processing_activity: unknown };
  return Boolean(processingActivity && typeof processingActivity === 'object' && 'id' in processingActivity);
};

export const parseAssetProcessingActivityDetailForFE = (dto: AssetProcessingActivityDetailDTO): AssetProcessingActivityDetail => ({
  id: dto.id,
  revision: dto.revision,
  updatedAt: dto.updated_at,
  completenessStatus: dto.completeness_status,
  changedAfterSubmission: dto.changed_after_submission,
  dpiaIndicationStatus: dto.dpia_indication_status,
  dpiaIndicationComment: dto.dpia_indication_comment,
  minimumSecurityAssessment: dto.minimum_security_assessment,
  minimumSecurityAssessmentComment: dto.minimum_security_assessment_comment,
  handlesSpecialCategories: dto.handles_special_categories,
  handlesProcessingOutsideEuEea: dto.handles_processing_outside_eu_eea,
  handlesSensitivePersonalData: dto.handles_sensitive_personal_data,
  purposes: (dto.purposes ?? []).map(parseOwnerAssetProcessingPurposeForFE),
  countries: (dto.countries ?? []).map(parseOwnerCountryHandlingForFE),
  processingActivity: parseOwnerProcessingActivityForFE(dto.processing_activity),
  review: parseOwnerAssetProcessingActivityReviewMetaForFE(dto.review),
});

export const parseAssetProcessingActivityUpdateForBE = (
  payload: AssetProcessingActivityUpdateInput,
): PatchedAssetProcessingActivityDTO => ({
  revision: payload.revision,
  ...(payload.dpiaIndicationStatus !== undefined ? { dpia_indication_status: payload.dpiaIndicationStatus } : {}),
  ...(payload.dpiaIndicationComment !== undefined ? { dpia_indication_comment: payload.dpiaIndicationComment } : {}),
  ...(payload.minimumSecurityAssessment !== undefined ? { minimum_security_assessment: payload.minimumSecurityAssessment } : {}),
  ...(payload.minimumSecurityAssessmentComment !== undefined
    ? { minimum_security_assessment_comment: payload.minimumSecurityAssessmentComment }
    : {}),
  ...(payload.handlesSpecialCategories !== undefined ? { handles_special_categories: payload.handlesSpecialCategories } : {}),
  ...(payload.handlesProcessingOutsideEuEea !== undefined
    ? { handles_processing_outside_eu_eea: payload.handlesProcessingOutsideEuEea }
    : {}),
  ...(payload.handlesSensitivePersonalData !== undefined ? { handles_sensitive_personal_data: payload.handlesSensitivePersonalData } : {}),
  ...(payload.updatedAt !== undefined ? { updated_at: payload.updatedAt } : {}),
});

export const parseOwnerAssetProcessingPurposeUpdateForBE = (
  payload: OwnerAssetProcessingPurposeUpdateInput,
): OwnerAssetProcessingPurposeCreateOrUpdateDTO => ({
  revision: payload.revision,
  ...(payload.applicabilityStatus !== undefined ? { applicability_status: payload.applicabilityStatus } : {}),
  ...(payload.applicabilityJustification !== undefined ? { applicability_justification: payload.applicabilityJustification } : {}),
  ...(payload.retentionTrigger !== undefined ? { retention_trigger: payload.retentionTrigger } : {}),
  ...(payload.retentionValue !== undefined ? { retention_value: payload.retentionValue } : {}),
  ...(payload.retentionUnit !== undefined ? { retention_unit: payload.retentionUnit } : {}),
  ...(payload.retentionComment !== undefined ? { retention_comment: payload.retentionComment } : {}),
  ...(payload.deletionOrAnonymisationRoutine !== undefined
    ? { deletion_or_anonymisation_routine: payload.deletionOrAnonymisationRoutine }
    : {}),
});

export const readRevisionFromPatchResponse = (data: unknown): number | null => {
  if (!data || typeof data !== 'object' || !('revision' in data)) {
    return null;
  }

  const { revision } = data as { revision: unknown };
  return typeof revision === 'number' ? revision : null;
};

export const parseOwnerCountryHandlingCreateForBE = (payload: OwnerCountryHandlingCreateInput): OwnerCountryHandlingCreateDTO => ({
  revision: payload.revision,
  country_code: payload.countryCode,
  handling_type: payload.handlingType,
  volume: payload.volume,
});

export const parseOwnerCountryHandlingDeleteForBE = (payload: OwnerCountryHandlingDeleteInput): OwnerCountryHandlingDeleteDTO => ({
  revision: payload.revision,
});

export const parseSoftDeleteAssetProcessingActivityForBE = (
  payload: SoftDeleteAssetProcessingActivityInput,
): SoftDeleteAssetProcessingActivityDTO => ({
  deletion_reason: payload.deletionReason,
});

export const parseAssetProcessingActivitySubmitForBE = (
  payload: AssetProcessingActivitySubmitInput = {},
): AssetProcessingActivitySubmitDTO => ({
  ...(payload.updatedAt !== undefined ? { updated_at: payload.updatedAt } : {}),
  ...(payload.revision !== undefined ? { revision: payload.revision } : {}),
});

export const parseAddAssetProcessingActivityForBE = (payload: AddAssetProcessingActivityInput) => ({
  processing_activity_id: payload.processingActivityId,
});
